import React, { useState, useRef } from 'react';
import { FaFacebookF, FaLinkedinIn, FaInstagram } from 'react-icons/fa';
import ReCAPTCHA from 'react-google-recaptcha';

const Footer = () => {
  const recaptchaRef = useRef();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    recaptchaToken: ''
  });

  const handleRecaptchaChange = (token) => {
    setFormData(prev => ({ ...prev, recaptchaToken: token }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.recaptchaToken) {
      alert('Please complete the reCAPTCHA');
      return;
    }
    try {
      const response = await fetch('/api/subscribe-newsletter', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      
      if (response.ok) {
        alert('Successfully subscribed to newsletter!');
        setFormData({ name: '', email: '', recaptchaToken: '' });
        recaptchaRef.current.reset();
      } else {
        throw new Error('Failed to subscribe');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('Failed to subscribe. Please try again later.');
    }
  };

  return (
    <footer className="bg-primary text-white p-12">
      <div className="container mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
          {/* Left Section */}
          <div className="space-y-8">
            <div>
              <h2 className="text-xl font-bold mb-4 bg-white text-purple-900 inline-block px-6 py-2 rounded-full">
                LOCATIONS
              </h2>
              <div className="space-y-6 mt-6">
                <div>
                  <h3 className="text-yellow-400 font-bold text-lg">SOUTH AUSTRALIA (HEAD OFFICE):</h3>
                  <p className="flex items-center mt-2">
                    Crowne Plaza, Level 1, 1-3 Metro Parade, Mawson Lakes, South Australia, 5095
                  </p>
                </div>
                <div>
                  <h3 className="text-yellow-400 font-bold text-lg">QUEENSLAND (HEAD OFFICE):</h3>
                  <p className="flex items-center mt-2">
                    23 Brandon Street, Marsden, Queensland, 4132
                  </p>
                </div>
              </div>
            </div>

            <div>
              <h2 className="text-xl font-bold mb-4 bg-white text-purple-900 inline-block px-6 py-2 rounded-full">
                NATIONAL CONTACT LINE
              </h2>
              <div className="space-y-4 mt-4">
                <p className="flex items-center">
                  <a href="tel:1300522786" className="hover:text-yellow-400 transition-colors">1300 522 786</a>
                </p>
                <p className="flex items-center">
                  <a href="mailto:info@livabilitycare.com.au" className="hover:text-yellow-400 transition-colors">info@livabilitycare.com.au</a>
                </p>
              </div>
            </div>

            <div>
              <h2 className="text-xl font-bold mb-4 bg-white text-purple-900 inline-block px-6 py-2 rounded-full">
                QUICK LINKS
              </h2>
              <div className="space-y-4 mt-4">
                <p className="flex items-center">
                  <a href="/news" className="hover:text-yellow-400 transition-colors">News</a>
                </p>
                <p className="flex items-center">
                  <a href="/careers" className="hover:text-yellow-400 transition-colors">Careers</a>
                </p>
                <p className="flex items-center">
                  <a href="/properties" className="hover:text-yellow-400 transition-colors">Vacancies</a>
                </p>
              </div>
            </div>
          </div>

          {/* Right Section */}
          <div className="space-y-8">
            <div>
              <h2 className="text-xl font-bold mb-4 bg-white text-purple-900 inline-block px-6 py-2 rounded-full">
                STAY UPDATED
              </h2>
              <div className="mt-4">
                <h3 className="text-lg mb-4 font-bold">Join our email newsletter to receive our latest news</h3>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="flex flex-row gap-5">
                  <input
                    type="text"
                    placeholder="Name"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full p-3 h-14 rounded-xl text-black"
                  />
                  <input
                    type="email"
                    placeholder="Email Address"
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    className="w-full p-3 h-14 rounded-xl text-black"
                  />
                  </div>
                  <div className="flex justify-start">
                    <ReCAPTCHA
                      ref={recaptchaRef}
                      sitekey="6Lcu-tMqAAAAAGwHN42PvnenVU6lYdAh_ikQfuV7"
                      onChange={handleRecaptchaChange}
                    />
                  </div>
                  <button
                    type="submit"
                    className="bg-secondary-green text-white px-16 py-2 rounded-full hover:bg-green-600 transition-colors"
                  >
                    SUBMIT
                  </button>
                </form>
              </div>
            </div>

            <div>
              <h2 className="text-xl font-bold mb-4">ACKNOWLEDGEMENT TO COUNTRY</h2>
              <div className="flex gap-6">
                <img
                  src="/Australian_Aboriginal_Flag-1.png"
                  alt="Aboriginal Flag"
                  className="w-auto h-24"
                />
                <div className="space-y-4 font-semibold">
                  <p>
                    We acknowledge this land that we meet on is the traditional lands of the Kaurna people and that we respect their spiritual relationship with their country.
                  </p>
                  <p>
                    We also acknowledge the Kaurna people as the custodians of the Adelaide region and that their cultural and heritage beliefs are still as important to the living Kaurna people today.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="mt-12 pt-8 border-t border-white/20">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="text-primary-yellow font-bold text-lg">
              <span>ABN: <span className='text-white'>85 628 259 281</span></span>
              <span className="mx-4">|</span>
              <span>NDIS Registration: <span className='text-white'>4- CVD371Z</span></span>
            </div>
            <div className="flex gap-4">
              {[FaFacebookF, FaLinkedinIn, FaInstagram].map((Icon, index) => {
                const socialLinks = [
                  'https://www.facebook.com/livabilitycareaustralia/',
                  'https://www.linkedin.com/company/livabilitycare/',
                  'https://www.instagram.com/livabilitycare/'
                ];
                return (
                  <a
                    key={index}
                    href={socialLinks[index]}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-8 h-8 rounded-full bg-white flex items-center justify-center"
                  >
                    <Icon className="text-purple-900 w-4 h-4" />
                  </a>
                );
              })}
            </div>
          </div>
          <p className="text-center mt-6 text-sm font-semibold">
            Content, including images, displayed on this website is protected by copyright laws. Downloading, republication, retransmission or reproduction of content on this website is strictly prohibited.<br></br> Developed by <a className="underline" href="https://www.concavetech.com.au/" target="_blank" rel="noopener noreferrer">Concave Tech</a>
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;